'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';

interface ConsultantProfile {
  id: string;
  email: string;
  full_name: string;
  cabinet: string;
  created_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  consultantProfile: ConsultantProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { fullName?: string; cabinet?: string }) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<ConsultantProfile | null>;
  // Collaborateur session management
  createCollaborateurSession: (campaignId: string) => Promise<{ sessionToken: string; sessionId: string } | null>;
  getCollaborateurSession: (sessionToken: string) => Promise<any>;
  updateCollaborateurSession: (sessionToken: string, responses: Record<string, any>, completed?: boolean) => Promise<void>;
  validateCampaignCode: (code: string) => Promise<any>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function generateSessionToken(): string {
  const array = new Uint8Array(32);
  if (typeof window !== 'undefined' && window.crypto) {
    window.crypto.getRandomValues(array);
  }
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [consultantProfile, setConsultantProfile] = useState<ConsultantProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchConsultantProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (!error && data) {
        setConsultantProfile(data as ConsultantProfile);
      }
    } catch {
      // Profile not yet created — ignore
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchConsultantProfile(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchConsultantProfile(session.user.id);
      } else {
        setConsultantProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Consultant Auth ──────────────────────────────────────────

  const signUp = async (email: string, password: string, metadata: { fullName?: string; cabinet?: string } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          cabinet: metadata?.cabinet || '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setConsultantProfile(null);
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<ConsultantProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('consultant_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data as ConsultantProfile | null;
  };

  // ── Collaborateur Session Management ────────────────────────

  const validateCampaignCode = async (code: string) => {
    const { data, error } = await supabase
      .from('campaigns')
      .select('id, campaign_code, campaign_name, org_name, expires_at, status, hierarchy_levels, structure')
      .eq('campaign_code', code.toUpperCase().trim())
      .eq('status', 'active')
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const createCollaborateurSession = async (
    campaignId: string
  ): Promise<{ sessionToken: string; sessionId: string } | null> => {
    const sessionToken = generateSessionToken();
    const { data, error } = await supabase
      .from('collaborateur_sessions')
      .insert({
        campaign_id: campaignId,
        session_token: sessionToken,
        responses: {},
      })
      .select('id')
      .single();
    if (error) throw error;
    // Store token in sessionStorage for the collaborateur's browser session
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('wbos_collab_token', sessionToken);
      sessionStorage.setItem('wbos_collab_session_id', data.id);
    }
    return { sessionToken, sessionId: data.id };
  };

  const getCollaborateurSession = async (sessionToken: string) => {
    const { data, error } = await supabase
      .from('collaborateur_sessions')
      .select('*, campaigns(campaign_code, campaign_name, org_name)')
      .eq('session_token', sessionToken)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const updateCollaborateurSession = async (
    sessionToken: string,
    responses: Record<string, any>,
    completed = false
  ) => {
    const updateData: any = { responses };
    if (completed) {
      updateData.completed_at = new Date().toISOString();
    }
    const { error } = await supabase
      .from('collaborateur_sessions')
      .update(updateData)
      .eq('session_token', sessionToken);
    if (error) throw error;
  };

  const value: AuthContextType = {
    user,
    session,
    consultantProfile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    createCollaborateurSession,
    getCollaborateurSession,
    updateCollaborateurSession,
    validateCampaignCode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
